import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import axios from 'axios';
import {
  Expense,
  ExpenseCategory,
  ExpenseMode,
  ExpenseStatus,
} from '../../../../interfaces/delivery-company/expenses';
import { useSnackBar } from '../../../../contexts/snackbar';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
} from '@mui/material';
import { displayMoney } from '../../../../utils/money';
import { GenericList } from '../../../common/generic-list/generic-list';
import { format } from 'date-fns';
import { CANCELLED } from 'dns';
import { showExpenseState } from '../../../common/styles/expenses.styles';
import {
  AssignmentLate,
  Checklist,
  Label,
  PointOfSale,
  Replay,
  Replay10,
} from '@mui/icons-material';
import { IconWithText } from '../../../common/styles/components';
import ExpenseFormDialog from './expense-form-dialog';
import { appendToPaginatedItems } from '../../../../utils/append_to_paginated_items';
import { PaginationContext } from '../../../../contexts/pagination-context';
import ManageCategories from './manage-labels';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import FilterChips from '../../../common/filter-chips/filter-chips';
import Grid2 from '@mui/material/Unstable_Grid2';
import DoubleDatePicker from '../../../common/double-date-picker/double-date-picker';
import DoubleDateOrdersFilter from '../../../common/double-date-orders-filter/double-date-orders-filter';
import {
  DoubleDate,
  DoubleDateMode,
  DoubleDateWithMode,
} from '../../../common/double-date-picker/double-date-picker.interfaces';
import { generateDateFilterQueryParam } from '../../../common/double-date-orders-filter/utils';
import ExpensesPieChart, { Summary } from './pie-chart';
import ExpensesSummary from './pie-chart';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../../utils/permissions';

function Expenses() {
  const { user, config } = useContext(AuthContext);
  const [expenses, setExpenses] = useState<Expense[]>();
  const [expensesCount, setExpensesCount] = useState<number>(0);
  const [addingExpense, setAddingExpense] = useState(false);
  const [summary, setSummary] = useState<Summary>();
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [modifyingExpense, setModifyingExpense] = useState<Expense | null>(
    null
  );
  const [typeFilter, setTypeFilter] = useState<ExpenseMode[]>([]);
  const [expenseCategoryFilters, setExpenseCategoryFilters] = useState<ExpenseCategory[]>([]);
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus[]>([
    ExpenseStatus.PENDING,
  ]);

  const [categories, setCategories] = useState<ExpenseCategory[]>([])

  const [isLoading, setIsloading] = useState(false);

  const [dateFilter, setDateFilter] = useState<DoubleDateWithMode>();
  const [managingLabels, setManagingLabels] = useState(false);
  const [confirmPayingExpense, setConfirmPayingExpense] =
    useState<Expense | null>(null);

  const [confirmCancellation, setConfirmCancellation] =
    useState<Expense | null>(null);
  const [operationInProgress, setOperationInProgress] = useState(0);

  const { showMessage } = useSnackBar();

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
    searchColumn: 'Order_Id',
    searchValue: '',
    searchOperation: 'equals',
  });

  const fetchSummary = async () => {
    try {
      setIsLoadingSummary(true);
      const endpoint = `${API_ENDPOINT}/v1/expenses/aggregated/?${generateDateFilterQueryParam(dateFilter)}`;
      const response = await axios.get<Summary>(endpoint, config);
      setSummary(response.data);
    } catch {
      showMessage('Failed to load charts', 'error');
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const handleFetchCategories = async () => {
    try {
      return (
        await axios.get<{ count: number; results: ExpenseCategory[] }>(
          `${API_ENDPOINT}/v1/expense-categories/`,
          config
        )
      ).data.results;
    } catch (e) {
      showMessage('Failed to load labels', 'error');
    }
    return [];
  };

  useEffect(()=> {
    const getCategories = async () => {
      const categories = await handleFetchCategories()
      setCategories(categories)
    }
    getCategories()
  }, [])
  useEffect(() => {
    const fetchExpenses = async () => {
      setIsloading(true);
      let endpoint = `${API_ENDPOINT}/v1/expenses/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}`;

      endpoint += `&${generateDateFilterQueryParam(dateFilter)}`;

      if (statusFilter.length > 0) {
        endpoint += `&status=${encodeURIComponent(statusFilter.join(','))}`;
      }

      if (typeFilter.length > 0) {
        endpoint += `&mode=${typeFilter}`;
      }
      if (expenseCategoryFilters.length > 0) {
        endpoint += `&category=${expenseCategoryFilters.map(cat => cat.id).join(",")}`;
      }

      try {
        const response = await axios.get<{ count: number; results: Expense[] }>(
          endpoint,
          config
        );
        setExpenses(response.data.results);
        setExpensesCount(response.data.count);
      } catch (e) {
        showMessage(
          'Failed to load expenses. Please try again later.',
          'error'
        );
      } finally {
        setIsloading(false);
      }
    };
    fetchExpenses();
  }, [
    JSON.stringify(dateFilter),
    statusFilter,
    typeFilter,
    JSON.stringify(controller),
    expenseCategoryFilters,
  ]);

  useEffect(() => {
    fetchSummary();
  }, [JSON.stringify(dateFilter)]);

  const handlePayment = async (expense: Expense) => {
    try {
      setOperationInProgress(expense.id);
      const response = await axios.post<Expense>(
        `${API_ENDPOINT}/v1/expenses/${expense.id}/pay/`,
        {},
        config
      );
      showMessage('Payment registered successfully');
      setExpenses(
        expenses!.map((item) => (item.id == expense.id ? response.data : item))
      );
      fetchSummary();
    } catch {
      showMessage('Failed to confirm payment.', 'error');
    } finally {
      setOperationInProgress(0);
    }
  };
  
  const handleCancellation = async (expense: Expense) => {
    try {
      setOperationInProgress(expense.id);
      const response = await axios.post<Expense>(
        `${API_ENDPOINT}/v1/expenses/${expense.id}/cancel/`,
        {},
        config
      );
      showMessage('Payment cancelled successfully');
      setExpenses(
        expenses!.map((item) => (item.id == expense.id ? response.data : item))
      );
      fetchSummary();
    } catch {
      showMessage('Failed to cancel expense.', 'error');
    } finally {
      setOperationInProgress(0);
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  return (
    <div>
      <div
        style={{
          width: '100%',
          gap: 6,
          justifyContent: 'end',
          display: 'flex',
        }}
      >
        <Button variant="contained" onClick={() => setAddingExpense(true)}>
          New Expense
        </Button>
        <Button
          sx={{ pl: 1 }}
          variant="outlined"
          onClick={() => setManagingLabels(true)}
          disabled={!user_has_data_model_access(user, DataResources.EXPENSE_CATEGORIES, ResourceActionType.VIEW)}
        >
          <Label fontSize="small" sx={{ mr: 1 }} /> Expense Categories
        </Button>
      </div>
      <br />

      <Grid2 container spacing={4} alignItems={"center"}>
        <Grid2 xs={12} md={9}>
          <DoubleDateOrdersFilter
            headless
            modes={[DoubleDateMode.CREATION_DATE, DoubleDateMode.PAYMENT_DATE]}
            dateFilterAction={function (d: DoubleDateWithMode): void {
              setDateFilter(d);
            }}
          />
        </Grid2>
        <Grid2 xs={12} md={4}>
          <FormControl fullWidth>
            <Autocomplete
              options={categories} // null = 'All'
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.id === value.id
              }
              multiple
              value={expenseCategoryFilters}
              onChange={(event, newValue) => {
                setExpenseCategoryFilters(newValue);
              }}
              disabled={isLoading}
              sx={{ maxWidth: 400 }}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  variant="outlined"
                />
              )}
            />
          </FormControl>
         
        </Grid2>
        <Grid2 xs={12} md={4}>
          <FilterChips
            centered
            label={'Type'}
            options={Array.from(Object.values(ExpenseMode)).filter(
              (item) => item != ExpenseMode.SALARY
            )}
            selected={typeFilter}
            multiple
            onChange={(selected) => {
              setTypeFilter(selected as ExpenseMode[]);
            }}
          />
        </Grid2>
        <Grid2 xs={12} md={4}>
          <FilterChips
            centered
            label={'Status'}
            multiple
            options={Object.values(ExpenseStatus)}
            selected={statusFilter}
            onChange={(selected) => {
              setStatusFilter(selected as ExpenseStatus[]);
            }}
          />
        </Grid2>
      </Grid2>

      <br />
      <ExpensesSummary
        dateFilter={dateFilter as DoubleDate}
        summary={summary}
      />

      <br />
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Chip
          color="primary"
          label={`${expensesCount} Expenses`}
          variant={'outlined'}
        />
      </div>

      <br />
      <GenericList<Expense>
        sourceView="expenses"
        items={expenses ?? []}
        selectedItems={new Map()}
        isLoading={isLoading}
        fullWidth
        columns={[
          {
            id: 'id',
            header: 'Expense #',
            display_body: (expense) => (
              <>
                {expense.status == ExpenseStatus.PENDING ? (
                  <div
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setModifyingExpense(expense)}
                  >
                    <b>{expense.id}</b>
                  </div>
                ) : (
                  <b>{expense.id}</b>
                )}
                {operationInProgress == expense.id && <CircularProgress />}
              </>
            ),
          },
          {
            id: 'By',
            header: 'By',
            display_body: (item) => (
              <>
                {item.mode == ExpenseMode.MANUAL && (
                  <>
                    <div style={{ marginTop: '2px' }}>
                      {item.created_by?.name}
                    </div>
                  </>
                )}
                {item.mode == ExpenseMode.RECURRING && (
                  <Chip
                    label={`Recurrent: ${item.recurring_template?.name ?? 'No Name'}`}
                    icon={<Replay fontSize="small" />}
                  />
                )}

                 <br />
                 ↳ At {format(item.created_at!, 'dd-MM-yyyy HH:mm')}
              </>
            ),
          },
          {
            id: 'category',
            header: 'Category',
            display_body: (expense) => <Chip label={expense.category!.label} />,
          },
          {
            id: 'amount',
            header: 'Amount',
            display_body: (expense) => <>{displayMoney(expense.expense)}</>,
          },
          {
            id: 'status',
            header: 'Status',
            display_body: (expense) => (
              <>
                {' '}
                {showExpenseState(expense.status)}
                {expense.status == ExpenseStatus.PAID && (
                  <div style={{ marginTop: '2px' }}>
                    At {format(new Date(expense.paid_at!), 'dd-MM-yyyy HH:mm')}
                    <br /> by {expense.paid_by?.name}
                  </div>
                )}
                {expense.status == ExpenseStatus.CANCELLED && (
                  <div style={{ marginTop: '2px' }}>
                    At {format(new Date(expense.cancelled_at!), 'dd-MM-yyyy HH:mm')}
                    <br /> by {expense.cancelled_by?.name}
                  </div>
                )}
              </>
            ),
          },
          {
            id: 'due',
            header: 'Due Date',
            display_body: (expense) => (
              <>
                {expense.due_date && format(new Date(expense.due_date), 'dd-MM-yyyy')}
                {expense.is_overdue && (
                  <>
                    <br />
                    <IconWithText centered>
                      <AssignmentLate color="error" />{' '}
                      <span style={{ color: 'red' }}>Over Due</span>
                    </IconWithText>
                  </>
                )}
              </>
            ),
          },
          {
            id: 'details',
            header: 'Details',
            display_body: (expense) => expense.description,
          },
          {
            id: 'pay',
            header: 'Pay',
            display_body: (item) => {
              return (
                item.status == ExpenseStatus.PENDING && (
                  <Button
                    variant="contained"
                    onClick={() => setConfirmPayingExpense(item)}
                    disabled={operationInProgress > 0}
                  >
                    {' '}
                    Pay{' '}
                  </Button>
                )
              );
            },
          },
          {
            id: 'cancel',
            header: 'Cancel',
            display_body: (item) => {
              return (
                item.status == ExpenseStatus.PENDING && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => setConfirmCancellation(item)}
                    disabled={operationInProgress > 0}
                  >
                    {' '}
                    Cancel{' '}
                  </Button>
                )
              );
            },
          },
        ]}
        getItemUID={(expense) => expense.id.toString()}
        rowConfigs={[]}
      ></GenericList>

      <TablePagination
        component="div"
        onPageChange={handlePageChange}
        page={controller.page}
        count={expensesCount}
        rowsPerPage={controller.rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
        labelRowsPerPage=""
        style={{ overflow: 'visible' }}
      />
      {addingExpense && expenses && (
        <ExpenseFormDialog
          onSuccess={(e) => {
            setExpenses(appendToPaginatedItems(10, expenses, e));
            fetchSummary();
          }}
          onClose={() => setAddingExpense(false)}
          open={addingExpense}
        />
      )}

      {modifyingExpense && (
        <ExpenseFormDialog
          onClose={() => setModifyingExpense(null)}
          defaultExpense={modifyingExpense}
          onSuccess={(e) => {
            setExpenses(expenses!.map((item) => (item.id == e.id ? e : item)));
          }}
          open={modifyingExpense != null}
        />
      )}

      {managingLabels && (
        <ManageCategories onClose={() => setManagingLabels(false)} />
      )}

      {confirmPayingExpense && (
        <ConfirmAction
          message={`Do you confirm paying ${displayMoney(confirmPayingExpense.expense)} for ${confirmPayingExpense.category?.label} ?`}
          action={() => handlePayment(confirmPayingExpense)}
          isOpen={true}
          handleClose={() => setConfirmPayingExpense(null)}
          isSevereAction
          confirmSevereActionTarget={'pay'}
        >
          <Alert severity="info">This operation can not be undone</Alert>
        </ConfirmAction>
      )}

      {confirmCancellation && (
        <ConfirmAction
          message={`Do you confirm cancelling ${displayMoney(confirmCancellation.expense)} for ${confirmCancellation.category?.label} ?`}
          action={() => handleCancellation(confirmCancellation)}
          isOpen={true}
          handleClose={() => setConfirmCancellation(null)}
          isSevereAction
          confirmSevereActionTarget={'cancel expense'}
        >
          <Alert severity="info">This operation can not be undone</Alert>
        </ConfirmAction>
      )}
    </div>
  );
}

export default Expenses;
